import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { client } from "@/lib/api";

// WW-173 · chain bar, "more in this chain" and the chain page (D89). Each event still shows on its own.
const day = (s) => new Date(`${s}T12:00:00+03:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).toLowerCase();
const time = (s) => (s ? new Date(s).toLocaleTimeString("en-GB", { timeZone: "Africa/Nairobi", hour: "2-digit", minute: "2-digit" }) : "");

export const ChainBar = ({ chain }) => {
  if (!chain) return null;
  return (
    <Link to={`/app/chain/${chain.id}`} data-testid="event-chain-bar"
          className="mb-3 flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] font-semibold lowercase"
          style={{ background: "var(--ww-text)", color: "var(--ww-surface)", fontFamily: "Poppins" }}>
      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ border: "1px solid currentColor" }}>chain · {chain.event_count}</span>
      <span className="truncate">part of {(chain.name || "").toLowerCase()}</span>
    </Link>
  );
};

export const MoreInChain = ({ chain }) => {
  if (!chain || !(chain.more || []).length) return null;
  return (
    <section className="mt-8" data-testid="event-more-in-chain">
      <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11 }}>more in this chain</p>
      <ul className="mt-2 grid gap-2">
        {chain.more.map((e) => (
          <li key={e.id} className="min-w-0">
            <Link to={`/app/event/${e.id}`} data-testid={`chain-more-${e.id}`} className="flex items-center gap-3 rounded-xl border px-3 py-2"
                  style={{ borderColor: "var(--ww-line)" }}>
              <span className="w-6 text-[13px] font-bold">{e.number}</span>
              <span className="min-w-0 flex-1 truncate text-[14px] lowercase">{(e.title || "").toLowerCase()}</span>
              <span className="shrink-0 text-[12px]">{e.start_at ? `${day(e.start_at.slice(0, 10))} · ${time(e.start_at)}` : ""}{e.price ? ` · ${e.price}` : ""}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function ChainPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [pick, setPick] = useState(null);
  useEffect(() => {
    client.get(`/public/chains/${id}`).then((r) => {
      setC(r.data);
      const today = new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Nairobi" });
      const days = r.data.programme.map((d) => d.day);
      setPick(days.find((d) => d >= today) || days[0]);
    }).catch(() => setErr(true));
  }, [id]);
  if (err) return <p className="p-6" data-testid="chain-page-missing">this chain is not on watu wako.</p>;
  if (!c) return <p className="p-6">loading…</p>;
  const chosen = c.programme.find((d) => d.day === pick) || c.programme[0];
  return (
    <div className="mx-auto max-w-3xl px-4 py-6" data-testid="chain-page" style={{ color: "var(--ww-text)" }}>
      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ border: "1px solid currentColor" }}>
        chain · {c.event_count}{c.status === "ended" ? " · ended" : c.status === "on now" ? " · on now" : ""}
      </span>
      <h1 className="mt-2 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.03em", fontSize: 32, lineHeight: 1.05 }}>{(c.name || "").toLowerCase()}</h1>
      {c.one_line && <p className="mt-1 text-[14px]">{c.one_line}</p>}
      <div className="mt-4 flex gap-2 overflow-x-auto" data-testid="chain-days">
        {c.programme.map((d) => (
          <button key={d.day} type="button" data-testid={`chain-day-${d.day}`} onClick={() => setPick(d.day)}
                  className="shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold lowercase"
                  style={d.day === chosen.day ? { background: "#1A1A30", color: "#FFFFFF" } : { border: "1px solid var(--ww-line)" }}>
            {d.day === "no date" ? "no date" : day(d.day)}
          </button>
        ))}
      </div>
      <ul className="mt-4 grid gap-3" data-testid="chain-programme">
        {chosen.events.map((e) => (
          <li key={e.id} className="relative min-w-0">
            <Link to={`/app/event/${e.id}`} data-testid={`chain-page-event-${e.id}`} className="flex items-center gap-3 rounded-xl border px-3 py-3" style={{ borderColor: "var(--ww-line)" }}>
              <span className="w-6 text-[14px] font-bold">{e.number}</span>
              <span className="min-w-0 flex-1"><span className="block truncate text-[15px] lowercase">{(e.title || "").toLowerCase()}</span>
                <span className="block truncate text-[12px]">{time(e.start_at)} · {(e.venue_display || "").toLowerCase()}{e.price ? ` · ${e.price}` : ""}</span></span>
              {e.is_past && (
                <span data-testid="event-this-happened-stamp" aria-hidden="true"
                      style={{ transform: "rotate(-12deg)", fontFamily: "Poppins", fontWeight: 800, fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase",
                               color: "#FFFFFF", border: "2px solid #FFFFFF", borderRadius: 6, background: "rgba(26,26,48,.78)", padding: "3px 8px" }}>
                  this happened
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
